import { Navigate } from 'react-router-dom';
// project import
import Loadable from '~/components/LoadableErrorReload';
import RouteErrorBoundary from '~/components/RouteErrorBoundary';
import DashboardLayout from '~/layout/Dashboard';
import PagesLayout from '~/layout/Pages';
import { PagePolicies } from '~/utils/roles';

import PermCheck from '../utils/PermCheck';

// onboarding routes
const BuyRoute = Loadable(() => import('~/pages/auth/onboarding/routes/BuyRoute'));
const EarnFundingRoute = Loadable(() => import('~/pages/auth/onboarding/routes/EarnFundingRoute'));
const HitProfitTargetsRoute = Loadable(
  () => import('~/pages/auth/onboarding/routes/HitProfitTargetsRoute'),
);
const HowToTradeRoute = Loadable(() => import('~/pages/auth/onboarding/routes/HowToTradeRoute'));
const Onboarding = Loadable(() => import('~/pages/auth/onboarding/onboarding'));
const SellRoute = Loadable(() => import('~/pages/auth/onboarding/routes/SellRoute'));
const TradingBasicsRoute = Loadable(
  () => import('~/pages/auth/onboarding/routes/TradingBasicsRoute'),
);
const WelcomeRoute = Loadable(() => import('~/pages/auth/onboarding/routes/WelcomeRoute'));

// default layout
const DashboardDefault = Loadable(() => import('~/pages/dashboard/default'));

// end user pages
const ProfileDetails = Loadable(() => import('~/pages/dashboard/profile/detailsTab'));
const Billing = Loadable(() => import('~/pages/dashboard/billing'));
const Certificates = Loadable(() => import('~/pages/dashboard/certificates'));
const AccountsCrystal = Loadable(() => import('~/pages/dashboard/accounts/index.crystal'));
const NewAccountV2 = Loadable(() => import('~/pages/dashboard/purchase/newTradingCombineV2'));
const Addons = Loadable(() => import('~/pages/dashboard/accounts/addons'));
const AccountDetails = Loadable(() => import('~/pages/dashboard/accounts/details'));
const PolicySelection = Loadable(
  () =>
    import('~/pages/dashboard/accounts/components/xfaJourney/components/PolicySelection/PolicySelection'),
);
const DateDetails = Loadable(() => import('~/pages/dashboard/accounts/dateDetails'));
const Payouts = Loadable(() => import('~/pages/dashboard/payouts'));
const XFASuccess = Loadable(() => import('~/pages/dashboard/accounts/xfaSuccess'));
const Labs = Loadable(() => import('~/pages/labs'));

// public pages
const ConfirmEmail = Loadable(() => import('~/pages/public/confirmEmail'));
const ResetPassword = Loadable(() => import('~/pages/public/resetPassword'));
const AgreementPage = Loadable(() => import('~/pages/public/agreement'));
const ErrorPage = Loadable(() => import('~/pages/maintenance/404'));
const RedirectToBrokerage = Loadable(() => import('~/pages/public/redirectToBrokerage'));

const restrictPage = (page: React.ReactNode, premissions: string | string[]) => {
  return (
    <PermCheck permissions={premissions} fallback={<h1>You do not have access to this page.</h1>}>
      {page}
    </PermCheck>
  );
};

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/onboarding',
      element: <Onboarding />,
      children: [
        {
          path: 'trading-basics',
          element: <TradingBasicsRoute />,
        },
        {
          path: 'how-to-trade',
          element: <HowToTradeRoute />,
        },
        {
          path: 'welcome',
          element: <WelcomeRoute />,
        },
        {
          path: 'buy',
          element: <BuyRoute />,
        },
        {
          path: 'sell',
          element: <SellRoute />,
        },
        {
          path: 'hit-profit-targets',
          element: <HitProfitTargetsRoute />,
        },
        {
          path: 'earn-funding',
          element: <EarnFundingRoute />,
        },
      ],
    },
    {
      path: '/',
      element: <DashboardLayout />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          path: 'labs',
          element: restrictPage(<Labs />, PagePolicies.EndUserAccountsPage),
        },
        {
          path: 'dashboard',
          children: [
            {
              path: 'default',
              element: <DashboardDefault />,
            },
            {
              path: 'profile/details',
              element: restrictPage(<ProfileDetails />, PagePolicies.EndUserProfilePage),
            },
            {
              path: 'profile/billing',
              element: restrictPage(<Billing />, PagePolicies.EndUserProfilePage),
            },
            {
              path: 'accounts',
              element: restrictPage(<AccountsCrystal />, PagePolicies.EndUserAccountsPage),
            },
            {
              path: 'accounts/new',
              element: restrictPage(<NewAccountV2 />, PagePolicies.EndUserAccountsPage),
            },
            // TODO: remove once we've confirmed no external links still point at /accounts/new-v2
            {
              path: 'accounts/new-v2',
              element: <Navigate to="/dashboard/accounts/new" replace />,
            },
            {
              path: 'accounts/addons',
              element: restrictPage(<Addons />, PagePolicies.EndUserAccountsPage),
            },
            {
              path: 'accounts/xfa-success',
              element: restrictPage(<XFASuccess />, PagePolicies.EndUserAccountsPage),
            },
            {
              path: 'accounts/:id',
              element: restrictPage(<AccountDetails />, PagePolicies.EndUserAccountsPage),
            },
            {
              path: 'accounts/:id/select-policy',
              element: restrictPage(<PolicySelection />, PagePolicies.EndUserAccountsPage),
            },
            {
              path: 'accounts/:id/:date',
              element: restrictPage(<DateDetails />, PagePolicies.EndUserProfilePage),
            },
            {
              path: 'payouts',
              element: restrictPage(<Payouts />, PagePolicies.EndUserPayoutsPage),
            },
            {
              path: 'certificates',
              element: restrictPage(<Certificates />, PagePolicies.EndUserCertificatesPage),
            },
            {
              path: '*',
              element: <ErrorPage />,
            },
          ],
        },
      ],
    },
    {
      path: '/public',
      element: <PagesLayout />,
      children: [
        {
          path: 'confirm-email/:code',
          element: <ConfirmEmail />,
        },
        {
          path: 'redirect-to-brokerage',
          element: restrictPage(<RedirectToBrokerage />, PagePolicies.EndUserAccountsPage),
        },
        {
          path: 'agreement/:productType',
          element: <AgreementPage />,
        },
        {
          path: 'reset-password/:code',
          element: <ResetPassword />,
        },
        {
          path: '*',
          element: <ErrorPage />,
        },
      ],
    },
  ],
};

export default MainRoutes;
